import { PrinterIcon, Calendar } from 'lucide-react';
import { DateRange } from 'react-day-picker';
import React from 'react';
import { cn } from '../../../../lib/utils';
import { useRefresh } from '../../../../hooks/useRefresh';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../../components/ui/select';
import { useGetSalesMetrics } from '../queries/index';
import { formatAmount } from '../../../../utils/formatAmount';
import { captureException } from '../../../../integrations/sentry/capture';
import CustomDateRangePicker from '../../../date-range-picker/index';
import { Dialog, DialogContent, DialogTitle } from '../../../ui/dialog';
import Button from '../../../resuable-ui/button/index';

type ReportsFilterProps = {
	onDateSelect: (range: DateRange | undefined) => void;
	selectedDate: DateRange | undefined;
	downloadReport: () => void;
	salesData?: any[];
	onFilterBy?: (v: string) => void;
};

const ReportsFilter = ({ downloadReport, salesData, onFilterBy, onDateSelect, selectedDate }: ReportsFilterProps) => {
	const [selectedFilter, setSelectedFilter] = React.useState('today');

	const [showModal, setShowModal] = React.useState(false);

	const handleFilterChange = (value: string) => {
		if (value === 'customDate') {
			setShowModal(true);
			setSelectedFilter('customDate');
			return;
		}
		onDateSelect(undefined);

		setSelectedFilter(value);
		onFilterBy?.(value);
	};

	const { data: metricsData } = useGetSalesMetrics({
		salesFilter: selectedFilter,
		customDate: selectedDate,
	});

	const { Component: RefreshSalesReport } = useRefresh(['sales-report', 'sales-report-summary']);

	const [isDownloading, setIsDownloading] = React.useState(false);

	const promisifyDownloadReport = () => {
		return new Promise((resolve, reject) => {
			setTimeout(() => {
				resolve(downloadReport());
			}, 200);
		});
	};

	const handleDownloadReport = async () => {
		try {
			setIsDownloading(true);
			await promisifyDownloadReport();
		} catch (err) {
			captureException(err, { fn: 'ReportsFilter.handleDownloadReport' });
		} finally {
			setIsDownloading(false);
		}
	};

	return (
		<div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 md:gap-0  mt-[var(--navbar-height)] md:mt-0">
			<div className="flex flex-col md:flex-row md:items-center md:justify-start gap-3 md:gap-0 order-2 md:order-1">
				<div className="flex items-center">
					<div className="flex md:hidden">
						<Select value={selectedFilter} onValueChange={handleFilterChange}>
							<SelectTrigger className="w-full md:w-[140px] border-0">
								<SelectValue />
							</SelectTrigger>
							<SelectContent>
								<SelectItem value="today">Today</SelectItem>
								<SelectItem value="yesterday">Yesterday</SelectItem>
								<SelectItem value="thisWeek">This Week</SelectItem>
								<SelectItem value="lastWeek">Last Week</SelectItem>
								<SelectItem value="thisMonth">This Month</SelectItem>
								<SelectItem value="lastMonth">Last Month</SelectItem>
								<SelectItem value="thisYear">This Year</SelectItem>
								<SelectItem value="customDate">Custom Date</SelectItem>
							</SelectContent>
						</Select>
					</div>
					<div className="flex items-center justify-center flex-1">
						<h1 className="text-[0.95rem] font-bold flex self-center mt-2">Daily Sales Report</h1>
						{RefreshSalesReport}
					</div>
				</div>

				<div className="hidden md:flex items-center align-self-center h-[3vh] w-[1px] bg-[#C05621] mx-2"></div>

				<div className="hidden md:flex flex-col md:flex-row md:items-center md:ml-3 gap-2 md:gap-0">
					<Select value={selectedFilter} onValueChange={handleFilterChange}>
						<SelectTrigger className="w-full md:w-[140px] border-0">
							<SelectValue />
						</SelectTrigger>
						<SelectContent>
							<SelectItem value="today">Today</SelectItem>
							<SelectItem value="yesterday">Yesterday</SelectItem>
							<SelectItem value="thisWeek">This Week</SelectItem>
							<SelectItem value="lastWeek">Last Week</SelectItem>
							<SelectItem value="thisMonth">This Month</SelectItem>
							<SelectItem value="lastMonth">Last Month</SelectItem>
							<SelectItem value="thisYear">This Year</SelectItem>
							<SelectItem value="customDate">Custom Date</SelectItem>
						</SelectContent>
					</Select>

					<div className="w-full md:w-auto">
						{selectedDate && (
							<CustomDateRangePicker
								placeholder="Pick a date period"
								selectedRange={selectedDate}
								onRangeChange={onDateSelect}
								name="reports filter"
								icon={<Calendar size={14} />}
							/>
						)}
					</div>

					<Dialog open={showModal} onOpenChange={setShowModal}>
						<DialogContent className="bg-white p-6 max-w-md text-center">
							<DialogTitle className="text-lg font-semibold">Select Custom Date</DialogTitle>

							<CustomDateRangePicker
								placeholder="Pick a date period"
								selectedRange={selectedDate}
								onRangeChange={onDateSelect}
								name="reports filter"
								icon={<Calendar size={14} />}
							/>

							<div className="flex justify-center gap-4 mt-6">
								<Button
									className="w-fit border border-[#E2E8F0] bg-[#FEFBFF] px-4 py-2 text-[#718096]"
									type="button"
									variant="outline"
									onClick={() => {
										setShowModal(false);
										setSelectedFilter('today');
									}}
								>
									Close
								</Button>
								<Button
									className="bg-[#A63494] py-2 px-4 font-semibold text-xl text-center text-white"
									type="button"
									onClick={() => setShowModal(false)}
								>
									Done
								</Button>
							</div>
						</DialogContent>
					</Dialog>
				</div>

				<div className="flex flex-col sm:flex-row gap-4 sm:gap-6 md:ml-4 md:mt-2 order-3 md:order-none">
					<div className="flex items-center gap-2">
						<h3 className="text-[#4A5568] text-[0.875rem] font-[400]">Total Sale</h3>
						<div className="text-[#000000] font-[700] text-[18px]">{metricsData?.data?.totalTransactions ?? 0}</div>
					</div>

					<div className="flex items-center gap-2">
						<h3 className="text-[#4A5568] text-[0.875rem] font-[400]">Sale Total</h3>
						<div className="text-[#000000] font-[700] text-[18px]">
							₦{formatAmount(metricsData?.data?.paymentMethods?.reduce((p, v) => +p + v?.amount || 0, 0) ?? 0)}
						</div>
					</div>
				</div>
			</div>

			<div className="flex items-center justify-end order-1 md:order-2">
				<div className="flex items-center border border-[#E2E8F0] px-3 py-2 rounded-lg">
					<PrinterIcon size={14} className="text-[#A63494] mr-2" />

					<button
						onClick={handleDownloadReport}
						disabled={isDownloading || (salesData || [])?.length <= 0}
						className={cn('font-bold text-[0.875em] text-[#1A202C]', 'disabled:opacity-70 disabled:cursor-not-allowed')}
					>
						{isDownloading ? 'Downloading' : 'Download'}
					</button>
				</div>
			</div>
		</div>
	);
};

export default ReportsFilter;
